/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Grid, InputAdornment, Hidden, useMediaQuery } from '@material-ui/core';
import PropTypes from 'prop-types';
import FlightLandIcon from '@material-ui/icons/FlightLand';
import { TextField, Autocomplete } from '@rcg/ui-react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import Button from '../../../UiComponents/Button';
import DatepickerResponsive from '../DatepickerResponsive';
import CounterMenu from '../CounterMenu';
import useLdFlags from '../../../../hooks/useLdFlags';
import {
  SESSION_STORAGE_CHANGE_FLIGHTS,
  SESSION_STORAGE_SEARCH_BACK,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
  FLIGHT_TYPE_CODES,
  ENABLE_OPEN_SEARCH,
} from '../../../../utils/constants';
import InlineMenu from '../../../UiComponents/InlineMenu';
import useStyles from './useStyles';
import Theme from '../../../../utils/theme';
import Dictionary from '../../../../utils/dictionary';
import commonUtils, { emptyFunction } from '../../../../utils/common';
import {
  getSessionStorageItem,
  removeSessionStorageItem,
} from '../../../../operations/sessionStorage/utils';
import useDictionary from '../../../../hooks/useDictionary';

const FlightSearch = (props) => {
  const {
    setDepartureAirportCode,
    acOptions = [],
    hideIconOn = [],
    iconTo = <FlightLandIcon />,
    getSelectedDates = emptyFunction,
    flightToDefault = '',
    menuItems = [],
    labelFrom,
    labelTo,
    labelButton,
    noResultsErrorMessage,
    placeholderFrom,
    selectedDepartureAirport = emptyFunction,
    selectedArrivalAirport = emptyFunction,
    onSelectedGuests = emptyFunction,
    onSelectedFlightType = emptyFunction,
    incomingStartDate,
    incomingEndDate,
    incomingStartDefaultDate,
    incomingEndDefaultDate,
    minAvailableDate,
    maxAvailableDate,
    datepickerIcon,
    dividerInputIcon,
    labelDatepicker,
    resetDatesButtonLabel,
    placeholderStartDate,
    placeholderEndDate,
    incomingValue = {},
    showPassengersCounter,
    handleGuestsCounter,
    numberOfGuestCounter,
    ship,
    clearSearchResults = () => {},
    departureAirportRequiredError,
  } = props;

  const classes = useStyles();
  const getDictionary = useDictionary(Dictionary);
  const { ldFlags } = useLdFlags();

  const isOpenSearch = ldFlags?.[ENABLE_OPEN_SEARCH];

  const isSm = useMediaQuery(Theme.breakpoints.down('sm'));
  const isMd = useMediaQuery(Theme.breakpoints.down('md'));
  const flightTypeCodeFromSession = getSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE);
  const [isOneWay, setIsOneWay] = useState(flightTypeCodeFromSession === FLIGHT_TYPE_CODES.ONE_WAY);
  const [origin, setOrigin] = useState(incomingValue?.code || '');
  const [destination, setDestination] = useState(() => {
    const match = flightToDefault?.match(/\(([^)]+)\)/);
    return match ? match[1] : '';
  });

  let forceSplitInputs = false;
  let forceRangeInputs = false;
  const incomingStartDateFormat = dayjs(incomingStartDate).format('MMM DD');
  const incomingEndDateFormat = dayjs(incomingEndDate).format('MMM DD');
  const incomingEndDateYearFormat = dayjs(incomingEndDate).format('YYYY');
  const dateFormat = `${incomingStartDateFormat} to ${incomingEndDateFormat}, ${incomingEndDateYearFormat}`;

  const filteredAirportsForDestination = acOptions.filter((airport) => airport.code !== origin);

  const filteredAirportsForOrigin = acOptions.filter((airport) => airport.code !== destination);

  const isChangeFlight = getSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS);
  removeSessionStorageItem(SESSION_STORAGE_SEARCH_BACK);
  if (isSm) {
    forceRangeInputs = true;
  }

  if (isMd && !isSm) {
    forceSplitInputs = true;
    forceRangeInputs = false;
  }

  const getSelectedDepartureAirport = (value) => {
    setOrigin(value?.code);
    selectedDepartureAirport(value);
    if (setDepartureAirportCode && value) {
      setDepartureAirportCode(value.code);
    }
    clearSearchResults();
  };
  const handleGuestSelected = (value) => {
    onSelectedGuests(value);
  };

  const getSelectedArrivalAirport = (value) => {
    setDestination(value?.code);
    selectedArrivalAirport(value);
    clearSearchResults();
  };

  const handleOnSelectDate = (data) => {
    getSelectedDates(data);
  };

  const handleGuestsCounterComponent = (data) => {
    if (data !== numberOfGuestCounter) {
      handleGuestsCounter(data);
    }
  };

  const handleFlightTypeSelect = (value) => {
    const oneWayLabel = getDictionary(Dictionary.airFlightTypeOneWay.key);
    if (value.option === oneWayLabel) {
      setIsOneWay(true);
    } else {
      setIsOneWay(false);
    }
    onSelectedFlightType(value);
  };

  const getSelectedFlightType = () => {
    if (isOneWay || flightTypeCodeFromSession === FLIGHT_TYPE_CODES.ONE_WAY) {
      return {
        name: getDictionary(Dictionary.airFlightTypeOneWay.key),
        key: getDictionary(Dictionary.airFlightTypeOneWay.key),
      };
    }
    return null;
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} className={clsx(classes.inlineMenu, 'inlineMenu')}>
          {!showPassengersCounter ? (
            <InlineMenu
              disabled={Boolean(isChangeFlight)}
              disableCancelButton={isChangeFlight}
              selectedGuests={(value) => handleGuestSelected(value)}
              selectedOption={(value) => handleFlightTypeSelect(value)}
              menuData={menuItems}
              selectedFlightType={getSelectedFlightType()}
            />
          ) : (
            <CounterMenu
              className={classes.counterMenu}
              handleCount={(data) => handleGuestsCounterComponent(data)}
              lowLimit={1}
              defaultValue={numberOfGuestCounter}
              highLimit={4}
              singleLabel={getDictionary(Dictionary.airBrowsePageFormSingleLabel.key)}
              cancelLabel={getDictionary(Dictionary.airBrowsePageFormCancelButton.key)}
              typeLabel={getDictionary(Dictionary.airBrowsePageFormTypeLabel.key)}
              doneLabel={getDictionary(Dictionary.airBrowsePageFormCounterDoneButton.key)}
              multipleLabel={getDictionary(Dictionary.airBrowsePageFormMultipleLabel.key)}
              menuData={menuItems}
              selectedOption={(value) => handleFlightTypeSelect(value)}
              selectedFlightType={getSelectedFlightType()}
            />
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          xl={3}
          className={clsx(
            classes.gridItemInput,
            classes.gridItemInputFrom,
            'gridItemInput',
            departureAirportRequiredError ? 'required-error' : '',
          )}>
          <Autocomplete
            getSelectedValue={getSelectedDepartureAirport}
            getInputLabel={commonUtils.fliyingFromOptions}
            getOptionLabel={commonUtils.fliyingFromOptions}
            noResultsErrorMessage={noResultsErrorMessage}
            placeholder={placeholderFrom}
            label={labelFrom}
            options={filteredAirportsForOrigin}
            incomingValue={incomingValue?.code && commonUtils.fliyingFromOptions(incomingValue)}
          />
          {departureAirportRequiredError && (
            <div className={classes.requiredFieldError}>
              {getDictionary(Dictionary.airFormReservationFieldAirportRequiredError.key)}
            </div>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          xl={3}
          className={clsx(classes.gridItemInput, classes.gridItemInputTo, 'gridItemInput')}>
          {isOpenSearch ? (
            <Autocomplete
              getSelectedValue={getSelectedArrivalAirport}
              getInputLabel={commonUtils.fliyingFromOptions}
              getOptionLabel={commonUtils.fliyingFromOptions}
              noResultsErrorMessage={noResultsErrorMessage}
              placeholder={placeholderFrom}
              label={labelTo}
              options={filteredAirportsForDestination}
              incomingValue={flightToDefault || ''}
            />
          ) : (
            <TextField
              label={labelTo}
              disabled
              title={flightToDefault}
              value={flightToDefault}
              InputProps={{
                startAdornment: (
                  <Hidden only={hideIconOn}>
                    <InputAdornment position="start">{iconTo}</InputAdornment>
                  </Hidden>
                ),
              }}
            />
          )}
        </Grid>
        <Grid item xs={12} xl={4} className={clsx(classes.gridItemCalendar, 'gridItemCalendar')}>
          <DatepickerResponsive
            onSelectedDates={(data) => handleOnSelectDate(data)}
            incomingStartDate={incomingStartDate}
            incomingEndDate={incomingEndDate}
            labelStartDate={getDictionary(Dictionary.dataPickerLabelStartDate.key)}
            labelEndDate={getDictionary(Dictionary.datePieckerEndDateLabel.key)}
            backOptionLabel={getDictionary(Dictionary.datePickerBackLabel.key)}
            incomingStartDefaultDate={incomingStartDefaultDate}
            incomingEndDefaultDate={incomingEndDefaultDate}
            minAvailableDate={minAvailableDate}
            maxAvailableDate={isOneWay ? incomingStartDate : maxAvailableDate}
            datepickerIcon={datepickerIcon}
            dividerInputIcon={dividerInputIcon}
            yourDatesLabel={getDictionary(Dictionary.datePickerYourDatesLabel.key)}
            labelDatepicker={labelDatepicker}
            resetDatesButtonLabel={resetDatesButtonLabel}
            submitDatesButtonLabel={getDictionary(Dictionary.datePickerDoneButtonLabel.key)}
            placeholderStartDate={placeholderStartDate}
            placeholderEndDate={placeholderEndDate}
            forceSplitInputs={forceSplitInputs}
            forceRangeInputs={forceRangeInputs}
            // To allow single day select
            oneWay={isOneWay}
          />
        </Grid>
        <Grid
          item
          xs={12}
          lg={3}
          xl={2}
          className={clsx(classes.gridButtonWrapper, 'buttonWrapper')}>
          <Button
            data-testid="searchDesktop"
            className={classes.gridButton}
            type="submit"
            title={labelButton}
            variant="contained"
            color="primary">
            {labelButton}
          </Button>
        </Grid>
      </Grid>
      <div className="searchButton">
        <Grid
          item
          sm={12}
          className={clsx(classes.gridButtonMobileWrapper, 'gridButtonMobileWrapper')}>
          <Button
            data-testid="searchMobile"
            className={classes.gridButton}
            title={labelButton}
            type="submit"
            variant="contained"
            color="primary">
            {labelButton}
          </Button>
        </Grid>
      </div>
    </>
  );
};

FlightSearch.propTypes = {
  setDepartureAirportCode: PropTypes.func,
  acOptions: PropTypes.arrayOf(
    PropTypes.shape({
      city: PropTypes.string,
      code: PropTypes.string,
      country: PropTypes.string,
      name: PropTypes.string,
    }),
  ),
  // eslint-disable-next-line react/forbid-prop-types
  hideIconOn: PropTypes.array,
  iconTo: PropTypes.node,
  getSelectedDates: PropTypes.func,
  flightToDefault: PropTypes.string,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    }),
  ),
  labelFrom: PropTypes.string.isRequired,
  labelTo: PropTypes.string.isRequired,
  labelButton: PropTypes.string.isRequired,
  noResultsErrorMessage: PropTypes.string.isRequired,
  placeholderFrom: PropTypes.string.isRequired,
  selectedDepartureAirport: PropTypes.func,
  onSelectedGuests: PropTypes.func,
  onSelectedFlightType: PropTypes.func,
  incomingStartDate: PropTypes.string,
  incomingEndDate: PropTypes.string,
  incomingStartDefaultDate: PropTypes.string,
  incomingEndDefaultDate: PropTypes.string,
  minAvailableDate: PropTypes.string,
  maxAvailableDate: PropTypes.string,
  datepickerIcon: PropTypes.node,
  dividerInputIcon: PropTypes.node,
  labelDatepicker: PropTypes.string,
  resetDatesButtonLabel: PropTypes.string,
  placeholderStartDate: PropTypes.string,
  placeholderEndDate: PropTypes.string,
  incomingValue: PropTypes.shape({
    country: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
  }),
  showPassengersCounter: PropTypes.bool,
  handleGuestsCounter: PropTypes.func,
  numberOfGuestCounter: PropTypes.number,
  ship: PropTypes.string,
  departureAirportRequiredError: PropTypes.bool,
};

export default FlightSearch;
